import type * as React from 'react';
import type { ControlIcons } from 'owa-control-icons';
import type { LightningId } from 'owa-lightning-v2';
import type { AssetId, OutlookSpaceAsset, ProjectId } from 'owa-timestream-types';

export interface NewAsset<Payload = unknown, LargePayload = unknown> {
    type: string;
    width?: number;
    height?: number;
    payload: Payload;
    largePayload?: LargePayload;
    binaryPayload?: string; // base64
}

export type AssetFromClipboardData<Payload> = null | undefined | NewAsset<Payload>;
export type AssetsFromClipboardData<Payload> = AssetFromClipboardData<Payload>[];
export type AssetOrAssetsFromClipboardData<Payload = unknown> =
    | AssetFromClipboardData<Payload>
    | AssetsFromClipboardData<Payload>;
export type AssetOrAssetsOrPromiseFromClipboardData<Payload> =
    | AssetOrAssetsFromClipboardData<Payload>
    | Promise<AssetOrAssetsFromClipboardData<Payload>>;

export interface NewOrExistingAsset<Payload = unknown, LargePayload = unknown>
    extends NewAsset<Payload, LargePayload> {
    existingAssetId?: AssetId;
}

export interface AssetCommonViewProps<Payload = unknown> {
    payload: Payload;
    isDragPreview?: boolean;
    isSelected?: boolean;
    isNewlyCreated?: boolean;
    childrenAssets?: AssetId[];
    color?: string;
    DueDate?: React.ReactNode;
    Options?: React.ReactNode;
}

export enum DefaultCommand {
    Color,
    DueDate,
    Bucket,
    Resize,
}

export interface AssetResizeConfiguration {
    minWidth?: number;
    minHeight?: number;
    maxWidth?: number;
    maxHeight?: number;
    defaultWidth?: number;
    defaultHeight?: number;
    resizeDisabled?: boolean;
}

export interface AssetDescriptor<Payload = unknown> {
    readonly type: string;
    readonly iconName: ControlIcons;
    readonly iconNameRTL?: ControlIcons;
    readonly viewStyle?: string;
    readonly viewStyleDark?: string;
    readonly customRenderDueDate?: boolean;
    readonly customRenderOptions?: boolean;
    readonly unsupportedDefaultCommands: DefaultCommand[];

    // optional property to adjust the ellipsis menu for the asset
    readonly optionsPosition?: {
        top: number;
        right: number;
    };

    /**
     * Option to disable the asset's full control over it's corresponding task item.
     * This includes:
     * 1) avoiding deleting a task on due date or asset removal
     * 2) avoiding updating the task on asset title change
     */
    readonly preventFullControlOverTask?: boolean;

    /** Name of the asset type */
    readonly getToolName: () => string;

    /** Error message to display when the asset fails to load */
    readonly getErrorMessage: () => string;

    /** Tooltip shown for asset type in creation UX, e.g. "Add <asset-type>" */
    readonly getTooltip?: () => string;

    /** React component that renders an instance of this asset in the canvas. */
    readonly CanvasView: React.ComponentType<AssetCommonViewProps<Payload>>;

    /** Callback function for conditionally determining whether an asset should be registered (defaults to true) */
    readonly isAssetEnabled?: () => boolean;

    /** String to describe the particular asset by the narrator using the aria label property */
    readonly getAriaLabel: (payload: Payload) => string | undefined;

    /** Gets a CSS color string for the background of the card */
    readonly getBackgroundColor?: (payload: Payload) => string;

    /** Callback function for asset when color is set */
    readonly onSetColor?: (color: string, payload: Payload) => void;

    /** String to expose the title of the asset */
    readonly getTitleString: (payload: Payload) => string | undefined | null;

    /** String to expose this asset in the search box for quick switcher */
    readonly getSearchString: (payload: Payload) => string | undefined | null;

    /**
     * Function that produces new assets asynchronously.
     * This is invoked when the tool is clicked or dragged from creation UX and gives the tool
     * a chance to present a dialog, allowing the user to pre-configure the asset before it is saved.
     */
    readonly createAssets?: (
        pageX: number,
        pageY: number
    ) => Promise<AssetOrAssetsFromClipboardData<Payload>>;

    /**
     * Function that produces an empty asset.
     * This is invoked when the tool is clicked and/or dragged from creation UX.
     */
    readonly createEmptyAsset: () => NewAsset<Payload>;

    /**
     * Callback function for asset when invoked, for example when asset is focused and Enter key is hit
     */
    readonly onAssetInvoked?: (payload: Payload) => void;

    /**
     * Function that produces an asset from clipboard data.
     * This is invoked when a paste operation is performed by the end user.
     * The function must return null if data in the clipboard is not compatible.
     */
    readonly createAssetFromClipboardData?: (
        getData: (format: string) => string,
        items?: DataTransferItemList
    ) => AssetOrAssetsOrPromiseFromClipboardData<Payload>;

    /**
     * Function that initializes any application-wide asset data.
     */
    readonly initializeAssetData?: (projectId: ProjectId) => void;

    /**
     * Function that cleans up any application-wide asset data.
     */
    readonly cleanupAssetData?: () => void;

    /** Lightning info for Assets for which data is created in a different app */
    readonly lightningId?: LightningId;
    readonly inAppMessage?: string;

    /** Configuration for whether the asset can be resized, and by how much */
    readonly assetResizeConfiguration?: (payload: Payload) => AssetResizeConfiguration;

    readonly onProjectOpened?: (assets: OutlookSpaceAsset[]) => void;
}
